import { Box, Text, Tooltip } from '@wix/design-system';
import { CycleUtils, ECLIPSE_LIGHT_PRODUCT_FAMILY_ID } from '@wix/package-picker-shared';
import { ConfirmSmall } from '@wix/wix-ui-icons-common/lazy';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import React from 'react';
import { GiftIcon } from '../../GiftIcon';
import { useCurrentModule } from '../../ModuleProvider';
import { ECLIPSE_SALE_TEXT_COLOR, FREE_DOMAIN_CHECKOUT_SUMMARY_FIRST_YEAR_KEY, FREE_DOMAIN_CHECKOUT_SUMMARY_ONE_YEAR_KEY, SAVINGS_ON_CYCLE_KEY, SAVINGS_ON_CYCLE_TOOLTIP_KEY } from '../../../utils/consts';
import s from './SummaryBenefits.scss';
const InfoIcon = () => /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: "18",
  height: "18",
  viewBox: "0 0 18 18",
  fill: "none"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9 8.5C9.41421 8.5 9.75 8.83579 9.75 9.25V11.25C9.75 11.6642 9.41421 12 9 12C8.58579 12 8.25 11.6642 8.25 11.25V9.25C8.25 8.83579 8.58579 8.5 9 8.5Z",
  fill: ECLIPSE_SALE_TEXT_COLOR
}), /*#__PURE__*/React.createElement("path", {
  d: "M9 6.25C9.41421 6.25 9.75 6.58579 9.75 7C9.75 7.41421 9.41421 7.75 9 7.75C8.58579 7.75 8.25 7.41421 8.25 7C8.25 6.58579 8.58579 6.25 9 6.25Z",
  fill: ECLIPSE_SALE_TEXT_COLOR
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M9 3C12.31 3 15 5.69 15 9C15 12.31 12.31 15 9 15C5.69 15 3 12.31 3 9C3 5.69 5.69 3 9 3ZM9 4C6.24444 4 4 6.24444 4 9C4 11.7556 6.24444 14 9 14C11.7556 14 14 11.7556 14 9C14 6.24444 11.7556 4 9 4Z",
  fill: ECLIPSE_SALE_TEXT_COLOR
}));
export const SummaryBenefits = () => {
  var _cyclePricingData$cyc, _selectedCycle$cycleD, _selectedCycle$cycleD2, _savings$fixedAmount, _savings$fixedAmount2;
  const module = useCurrentModule();
  const {
    experiments
  } = module.internals.experiments.hooks.useExperiments();
  const selectedProductId = module.SDK.PackagePickerStore.useSelectedProductId();
  const selectedCycle = module.SDK.PackagePickerStore.useSelectedCycle();
  const {
    t
  } = module.internals.useTranslations();

  // Everything below is gated by this spec — including the data fetches, so
  // nothing runs for users who aren't in the experiment.
  const isSummaryBenefitLinesEnabled = experiments.isEnabled('summaryBenefitLines');
  const {
    data: currentProductStatus
  } = module.SDK.switchApiClient.hooks.useGetCurrentProductStatus({}, isSummaryBenefitLinesEnabled);
  const hasNoCurrentProduct = (currentProductStatus == null ? void 0 : currentProductStatus.status) === 'noCurrentProduct';
  const cycleId = selectedCycle ? CycleUtils.convertCycleToCycleId(selectedCycle) : undefined;
  const {
    data: cyclePricingData
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchCyclePricing({
    productId: selectedProductId ?? '',
    cycleId: cycleId
  }, Boolean(isSummaryBenefitLinesEnabled && selectedProductId && cycleId));
  const {
    data: selectedProduct
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({
    productId: selectedProductId ?? ''
  }, Boolean(isSummaryBenefitLinesEnabled && selectedProductId));
  const isLightPlan = (selectedProduct == null ? void 0 : selectedProduct.productFamilyId) === ECLIPSE_LIGHT_PRODUCT_FAMILY_ID;
  const savings = cyclePricingData == null || (_cyclePricingData$cyc = cyclePricingData.cyclePricing) == null ? void 0 : _cyclePricingData$cyc.savings;
  const isYearlyOrMultiYearCycle = (selectedCycle == null || (_selectedCycle$cycleD = selectedCycle.cycleDuration) == null ? void 0 : _selectedCycle$cycleD.unit) === PurchaseOffering.DurationUnit.YEAR;
  const yearCount = (selectedCycle == null || (_selectedCycle$cycleD2 = selectedCycle.cycleDuration) == null ? void 0 : _selectedCycle$cycleD2.count) ?? 0;
  const showSavingsLine = isYearlyOrMultiYearCycle && Boolean(savings == null || (_savings$fixedAmount = savings.fixedAmount) == null ? void 0 : _savings$fixedAmount.formattedAmount);
  const showFreeDomainLine = isYearlyOrMultiYearCycle && yearCount > 0;
  if (!isSummaryBenefitLinesEnabled) {
    return null;
  }
  if (!hasNoCurrentProduct) {
    return null;
  }
  if (isLightPlan) {
    return null;
  }
  if (!showSavingsLine && !showFreeDomainLine) {
    return null;
  }
  const savingsValues = {
    amount: savings == null || (_savings$fixedAmount2 = savings.fixedAmount) == null ? void 0 : _savings$fixedAmount2.formattedAmount,
    years: yearCount
  };
  const freeDomainKey = yearCount === 1 ? FREE_DOMAIN_CHECKOUT_SUMMARY_ONE_YEAR_KEY : FREE_DOMAIN_CHECKOUT_SUMMARY_FIRST_YEAR_KEY;
  return /*#__PURE__*/React.createElement(Box, {
    dataHook: "summary-benefits",
    direction: "vertical",
    gap: "4px",
    className: s.summaryBenefits,
    width: "100%"
  }, showSavingsLine && /*#__PURE__*/React.createElement(Box, {
    dataHook: "summary-benefits-savings",
    verticalAlign: "middle",
    gap: "4px"
  }, /*#__PURE__*/React.createElement(ConfirmSmall, {
    size: "18",
    color: ECLIPSE_SALE_TEXT_COLOR
  }), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    className: s.summaryBenefitsText
  }, t(SAVINGS_ON_CYCLE_KEY, savingsValues)), /*#__PURE__*/React.createElement(Tooltip, {
    className: s.summaryBenefitsTooltip,
    content: t(SAVINGS_ON_CYCLE_TOOLTIP_KEY, savingsValues),
    onShow: () => module.internals.bi.onSummaryBenefitsTooltipHover == null ? void 0 : module.internals.bi.onSummaryBenefitsTooltipHover({})
  }, /*#__PURE__*/React.createElement(Box, {
    className: s.summaryBenefitsInfoIcon
  }, /*#__PURE__*/React.createElement(InfoIcon, null)))), showFreeDomainLine && /*#__PURE__*/React.createElement(Box, {
    dataHook: "summary-benefits-free-domain",
    verticalAlign: "middle",
    gap: "4px"
  }, /*#__PURE__*/React.createElement(ConfirmSmall, {
    size: "18",
    color: ECLIPSE_SALE_TEXT_COLOR
  }), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    className: s.summaryBenefitsText
  }, t(freeDomainKey)), /*#__PURE__*/React.createElement(GiftIcon, null)));
};
//# sourceMappingURL=SummaryBenefits.js.map